/**
 * Página de Consultas do Sistema MEDICARE
 * Permite visualizar, criar e gerenciar consultas médicas
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  Badge,
  Avatar,
  useDisclosure,
  useColorModeValue,
  Tooltip,
  Card,
  CardBody,
  CardHeader,
  Grid,
  GridItem,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Textarea,
  Divider,
} from '@chakra-ui/react';
import {
  FiPlus,
  FiSearch,
  FiEye,
  FiEdit2,
  FiCalendar,
  FiClock,
  FiUser,
  FiFileText,
  FiFilter,
  FiDownload,
} from 'react-icons/fi';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { ConsultationsService, Consultation as ConsultationService } from '../../services/consultations';
import { StatusConsulta, TipoConsulta } from '../../types';

// Usando a interface do serviço
type Consultation = ConsultationService;

const CONSULTATION_STATUS = {
  agendada: { label: 'Agendada', color: 'blue' },
  em_andamento: { label: 'Em Andamento', color: 'yellow' },
  concluida: { label: 'Concluída', color: 'green' },
  cancelada: { label: 'Cancelada', color: 'red' },
};

const CONSULTATION_TYPES = {
  consulta: { label: 'Consulta', color: 'blue' },
  retorno: { label: 'Retorno', color: 'green' },
  emergencia: { label: 'Emergência', color: 'red' },
  exame: { label: 'Exame', color: 'purple' },
};

export const ConsultationsPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Debounce para o campo de busca
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);
  const [selectedConsultation, setSelectedConsultation] = useState<Consultation | null>(null);
  
  const { isOpen: isViewModalOpen, onOpen: onViewModalOpen, onClose: onViewModalClose } = useDisclosure();
  const { isOpen: isNewModalOpen, onOpen: onNewModalOpen, onClose: onNewModalClose } = useDisclosure();
  const { isOpen: isEditModalOpen, onOpen: onEditModalOpen, onClose: onEditModalClose } = useDisclosure();
  
  // Estados para formulários
  const [newConsultationForm, setNewConsultationForm] = useState({
    nome_paciente: '',
    prompt_usuario: '',
    data_consulta: '',
    tipo: 'consulta' as TipoConsulta,
    observacoes: ''
  });
  
  const [editConsultationForm, setEditConsultationForm] = useState({
    nome_paciente: '',
    prompt_usuario: '',
    data_consulta: '',
    tipo: 'consulta' as TipoConsulta,
    status: 'agendada' as StatusConsulta,
    observacoes: ''
  });
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const responseBgColor = useColorModeValue('gray.50', 'gray.700');
  const responseBorderColor = useColorModeValue('gray.200', 'gray.600');

  // Query para buscar consultas
  const {
    data: consultationsData,
    isLoading,
    error,
  } = useQuery(
    ['consultations', currentPage, debouncedSearchTerm, statusFilter, typeFilter],
    async () => {
      const filtros: any = {};
      
      if (debouncedSearchTerm) {
        filtros.busca = debouncedSearchTerm;
      }
      
      if (statusFilter) {
        filtros.status = statusFilter as StatusConsulta;
      }
      
      if (typeFilter) {
        filtros.tipo = typeFilter as TipoConsulta;
      }
      
      return await ConsultationsService.listar(currentPage, 10, filtros);
    },
    {
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000, // 5 minutos
    }
  );

  const consultations = consultationsData?.data || [];
  const totalPages = consultationsData?.totalPages || 1;
  const totalConsultations = consultationsData?.total || 0;

  // Estatísticas
  const stats = {
    total: totalConsultations,
    agendadas: consultations.filter(c => c.status === 'agendada').length,
    concluidas: consultations.filter(c => c.status === 'concluida').length,
    canceladas: consultations.filter(c => c.status === 'cancelada').length,
  };

  const handleViewConsultation = (consultation: Consultation) => {
    setSelectedConsultation(consultation);
    onViewModalOpen();
  };

  const handleNewConsultation = () => {
    console.log('Nova consulta clicada');
    setNewConsultationForm({
      nome_paciente: '',
      prompt_usuario: '',
      data_consulta: '',
      tipo: 'consulta' as TipoConsulta,
      observacoes: ''
    });
    onNewModalOpen();
  };

  const handleEditConsultation = (consultation?: Consultation) => {
    const consultationToEdit = consultation || selectedConsultation;
    console.log('Editar consulta clicada:', consultationToEdit?.id);
    if (consultationToEdit) {
      setSelectedConsultation(consultationToEdit);
      setEditConsultationForm({
        nome_paciente: consultationToEdit.nome_paciente || consultationToEdit.paciente?.nome || '',
        prompt_usuario: consultationToEdit.prompt_usuario || '',
        data_consulta: consultationToEdit.data_consulta || '',
        tipo: consultationToEdit.tipo || 'consulta',
        status: consultationToEdit.status || 'agendada',
        observacoes: ''
      });
      onEditModalOpen();
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('pt-BR'),
      time: date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
    };
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  // Função para salvar nova consulta
  const handleSaveNewConsultation = async () => {
    try {
      if (!user?.id) {
        alert('Usuário não autenticado');
        return;
      }

      const consultationData = {
        paciente_id: user.id, // Usando o ID do usuário logado como paciente
        medico_id: user.id, // Usando o ID do usuário logado como médico
        data_consulta: newConsultationForm.data_consulta,
        tipo: newConsultationForm.tipo,
        prompt_usuario: newConsultationForm.prompt_usuario
      };

      await ConsultationsService.criar(consultationData);
      alert('Consulta criada com sucesso!');
      onNewModalClose();
      // Recarregar a lista de consultas
      window.location.reload();
    } catch (error) {
      console.error('Erro ao criar consulta:', error);
      alert('Erro ao criar consulta. Tente novamente.');
    }
  };

  // Função para salvar edição de consulta
  const handleSaveEditConsultation = async () => {
    try {
      if (!selectedConsultation?.id) {
        alert('Consulta não selecionada');
        return;
      }

      const updateData = {
        data_consulta: editConsultationForm.data_consulta,
        tipo: editConsultationForm.tipo,
        status: editConsultationForm.status,
        prompt_usuario: editConsultationForm.prompt_usuario
      };

      await ConsultationsService.atualizar(selectedConsultation.id, updateData);
      alert('Consulta atualizada com sucesso!');
      onEditModalClose();
      // Recarregar a lista de consultas
      window.location.reload();
    } catch (error) {
      console.error('Erro ao atualizar consulta:', error);
      alert('Erro ao atualizar consulta. Tente novamente.');
    }
  };

  if (isLoading) {
    return <LoadingSpinner message="Carregando consultas..." />;
  }

  if (error) {
    return (
      <Container maxW="container.xl" py={8}>
        <Text color="red.500">Erro ao carregar consultas</Text>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={6} align="stretch">
        {/* Cabeçalho */}
        <Flex justify="space-between" align="center">
          <Heading size="lg">Consultas</Heading>
          <HStack spacing={3}>
            <Button
              leftIcon={<FiDownload />}
              variant="outline"
              size="sm"
            >
              Relatório
            </Button>
            <Button
              leftIcon={<FiPlus />}
              colorScheme="medicare"
              size="sm"
              onClick={handleNewConsultation}
              display="none"
            >
              Nova Consulta
            </Button>
          </HStack>
        </Flex>

        {/* Estatísticas */}
        <Grid templateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }} gap={4}>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Total</StatLabel>
                <StatNumber>{stats.total}</StatNumber>
                <StatHelpText>consultas</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Agendadas</StatLabel>
                <StatNumber color="blue.500">{stats.agendadas}</StatNumber>
                <StatHelpText>
                  <StatArrow type="increase" />
                  {((stats.agendadas / stats.total) * 100).toFixed(1)}%
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Concluídas</StatLabel>
                <StatNumber color="green.500">{stats.concluidas}</StatNumber>
                <StatHelpText>
                  {((stats.concluidas / stats.total) * 100).toFixed(1)}%
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Canceladas</StatLabel>
                <StatNumber color="red.500">{stats.canceladas}</StatNumber>
                <StatHelpText>
                  {((stats.canceladas / stats.total) * 100).toFixed(1)}%
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </Grid>

        {/* Filtros */}
        <Card>
          <CardBody>
            <HStack spacing={4} wrap="wrap">
              <InputGroup maxW="300px">
                <InputLeftElement>
                  <FiSearch color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Buscar por paciente, médico ou motivo..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
              
              <Select
                placeholder="Todos os status"
                maxW="200px"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="agendada">Agendada</option>
                <option value="em_andamento">Em Andamento</option>
                <option value="concluida">Concluída</option>
                <option value="cancelada">Cancelada</option>
              </Select>
              
              <Select
                placeholder="Todos os tipos"
                maxW="200px"
                value={typeFilter}
                onChange={(e) => {
                  setTypeFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="presencial">Presencial</option>
                <option value="telemedicina">Telemedicina</option>
              </Select>
              
              <Button
                leftIcon={<FiFilter />}
                variant="outline"
                onClick={() => {
                    setSearchTerm('');
                    setDebouncedSearchTerm('');
                    setStatusFilter('');
                    setTypeFilter('');
                    setCurrentPage(1);
                  }}
              >
                Limpar
              </Button>
            </HStack>
          </CardBody>
        </Card>

        {/* Tabela de consultas */}
        <Card>
          <CardHeader>
            <Text fontSize="md" fontWeight="medium">
              {consultations.length} consulta(s) encontrada(s) de {totalConsultations} total
            </Text>
          </CardHeader>
          <CardBody>
            <Box overflowX="auto">
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>Paciente</Th>
                    <Th>Médico</Th>
                    <Th>Data/Hora</Th>
                    <Th>Tipo</Th>
                    <Th>Status</Th>
                    <Th>Ações</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {consultations.map((consultation) => {
                    const dateTime = formatDateTime(consultation.data_hora || consultation.dataHora || consultation.data_consulta || '');
                    return (
                      <Tr key={consultation.id}>
                        <Td>
                          <HStack spacing={3}>
                            <Avatar
                              size="sm"
                              name={consultation.nome_paciente || consultation.paciente?.nome || 'Paciente'}
                              src={consultation.paciente?.avatar}
                            />
                            <Text fontWeight="medium">{consultation.nome_paciente || consultation.paciente?.nome || 'Paciente não encontrado'}</Text>
                          </HStack>
                        </Td>
                        <Td>
                          <Text fontWeight="medium">{consultation.usuario_nome || consultation.medico?.nome || 'Médico não encontrado'}</Text>
                        </Td>
                        <Td>
                          <VStack align="start" spacing={1}>
                            <HStack spacing={1}>
                              <FiCalendar size={14} />
                              <Text fontSize="sm">{dateTime.date}</Text>
                            </HStack>
                            <HStack spacing={1}>
                              <FiClock size={14} />
                              <Text fontSize="sm">{dateTime.time}</Text>
                            </HStack>
                          </VStack>
                        </Td>
                        <Td>
                          <Badge
                            colorScheme={CONSULTATION_TYPES[consultation.tipo]?.color || 'gray'}
                            variant="subtle"
                          >
                            {CONSULTATION_TYPES[consultation.tipo]?.label || consultation.tipo}
                          </Badge>
                        </Td>
                        <Td>
                          <Badge
                            colorScheme={CONSULTATION_STATUS[consultation.status].color}
                            variant="subtle"
                          >
                            {CONSULTATION_STATUS[consultation.status].label}
                          </Badge>
                        </Td>

                        <Td>
                          <HStack spacing={1}>
                            <Tooltip label="Visualizar">
                              <IconButton
                                aria-label="Visualizar consulta"
                                icon={<FiEye />}
                                size="sm"
                                variant="ghost"
                                onClick={() => handleViewConsultation(consultation)}
                              />
                            </Tooltip>
                            <Tooltip label="Editar">
                              <IconButton
                                aria-label="Editar consulta"
                                icon={<FiEdit2 />}
                                size="sm"
                                variant="ghost"
                                colorScheme="blue"
                                onClick={() => handleEditConsultation(consultation)}
                                display="none"
                              />
                            </Tooltip>
                          </HStack>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
            
            {/* Paginação */}
            {totalPages > 1 && (
              <Flex justify="center" align="center" mt={4} gap={2}>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  isDisabled={currentPage === 1}
                >
                  Anterior
                </Button>
                
                <HStack spacing={1}>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <Button
                        key={pageNum}
                        size="sm"
                        variant={currentPage === pageNum ? "solid" : "outline"}
                        colorScheme={currentPage === pageNum ? "medicare" : "gray"}
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </HStack>
                
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  isDisabled={currentPage === totalPages}
                >
                  Próxima
                </Button>
                
                <Text fontSize="sm" color="gray.500" ml={4}>
                  Página {currentPage} de {totalPages}
                </Text>
              </Flex>
            )}
          </CardBody>
        </Card>
      </VStack>

      {/* Modal de nova consulta */}
      <Modal isOpen={isNewModalOpen} onClose={onNewModalClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Nova Consulta</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Nome do Paciente</FormLabel>
                <Input
                  value={newConsultationForm.nome_paciente}
                  onChange={(e) => setNewConsultationForm(prev => ({ ...prev, nome_paciente: e.target.value }))}
                  placeholder="Digite o nome do paciente"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Data e Hora da Consulta</FormLabel>
                <Input
                  type="datetime-local"
                  value={newConsultationForm.data_consulta}
                  onChange={(e) => setNewConsultationForm(prev => ({ ...prev, data_consulta: e.target.value }))}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Tipo de Consulta</FormLabel>
                <Select
                  value={newConsultationForm.tipo}
                  onChange={(e) => setNewConsultationForm(prev => ({ ...prev, tipo: e.target.value as TipoConsulta }))}
                >
                  <option value="consulta">Consulta</option>
                  <option value="retorno">Retorno</option>
                  <option value="emergencia">Emergência</option>
                  <option value="exame">Exame</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Descrição/Motivo</FormLabel>
                <Textarea
                  value={newConsultationForm.prompt_usuario}
                  onChange={(e) => setNewConsultationForm(prev => ({ ...prev, prompt_usuario: e.target.value }))}
                  placeholder="Descreva o motivo da consulta ou sintomas"
                  rows={4}
                />
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="outline" mr={3} onClick={onNewModalClose}>
              Cancelar
            </Button>
            <Button colorScheme="medicare" onClick={handleSaveNewConsultation}>
              Criar Consulta
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal de edição de consulta */}
      <Modal isOpen={isEditModalOpen} onClose={onEditModalClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Editar Consulta</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Nome do Paciente</FormLabel>
                <Input
                  value={editConsultationForm.nome_paciente}
                  onChange={(e) => setEditConsultationForm(prev => ({ ...prev, nome_paciente: e.target.value }))}
                  placeholder="Digite o nome do paciente"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Data e Hora da Consulta</FormLabel>
                <Input
                  type="datetime-local"
                  value={editConsultationForm.data_consulta}
                  onChange={(e) => setEditConsultationForm(prev => ({ ...prev, data_consulta: e.target.value }))}
                />
              </FormControl>

              <Grid templateColumns="1fr 1fr" gap={4}>
                <FormControl isRequired>
                  <FormLabel>Tipo de Consulta</FormLabel>
                  <Select
                    value={editConsultationForm.tipo}
                    onChange={(e) => setEditConsultationForm(prev => ({ ...prev, tipo: e.target.value as TipoConsulta }))}
                  >
                    <option value="consulta">Consulta</option>
                    <option value="retorno">Retorno</option>
                    <option value="emergencia">Emergência</option>
                    <option value="exame">Exame</option>
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Status</FormLabel>
                  <Select
                    value={editConsultationForm.status}
                    onChange={(e) => setEditConsultationForm(prev => ({ ...prev, status: e.target.value as StatusConsulta }))}
                  >
                    <option value="agendada">Agendada</option>
                    <option value="em_andamento">Em Andamento</option>
                    <option value="concluida">Concluída</option>
                    <option value="cancelada">Cancelada</option>
                  </Select>
                </FormControl>
              </Grid>

              <FormControl>
                <FormLabel>Descrição/Motivo da Consulta</FormLabel>
                <Textarea
                  value={editConsultationForm.prompt_usuario}
                  onChange={(e) => setEditConsultationForm(prev => ({ ...prev, prompt_usuario: e.target.value }))}
                  placeholder="Descreva o motivo da consulta ou sintomas"
                  rows={4}
                />
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="outline" mr={3} onClick={onEditModalClose}>
              Cancelar
            </Button>
            <Button colorScheme="medicare" onClick={handleSaveEditConsultation}>
              Salvar Alterações
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal de visualização da consulta */}
      <Modal isOpen={isViewModalOpen} onClose={onViewModalClose} size="6xl">
        <ModalOverlay />
        <ModalContent maxW="60vw">
          <ModalHeader>Detalhes da Consulta</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedConsultation && (
              <VStack spacing={6} align="stretch">
                {/* Informações básicas */}
                <Grid templateColumns="1fr 1fr" gap={4}>
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.500">
                      Paciente
                    </Text>
                    <HStack spacing={3} mt={1}>
                      <Avatar
                        size="sm"
                        name={selectedConsultation?.nome_paciente || selectedConsultation?.paciente?.nome || 'Paciente'}
                        src={selectedConsultation?.paciente?.avatar}
                      />
                      <Text fontWeight="medium">{selectedConsultation?.nome_paciente || selectedConsultation?.paciente?.nome || 'Paciente não encontrado'}</Text>
                    </HStack>
                  </Box>
                  
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.500">
                      Médico
                    </Text>
                    <Text fontWeight="medium" mt={1}>{selectedConsultation?.usuario_nome || selectedConsultation?.medico?.nome || 'Médico não encontrado'}</Text>
                  </Box>
                </Grid>

                <Divider />

                {/* Data, hora e tipo */}
                <Grid templateColumns="1fr 1fr 1fr" gap={4}>
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.500">
                      Data
                    </Text>
                    <Text mt={1}>{formatDateTime(selectedConsultation?.data_hora || selectedConsultation?.dataHora || selectedConsultation?.data_consulta || '').date}</Text>
                  </Box>
                  
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.500">
                      Horário
                    </Text>
                    <Text mt={1}>{formatDateTime(selectedConsultation?.data_hora || selectedConsultation?.dataHora || selectedConsultation?.data_consulta || '').time}</Text>
                  </Box>
                  
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.500">
                      Tipo
                    </Text>
                    <Badge
                      colorScheme={CONSULTATION_TYPES[selectedConsultation.tipo].color}
                      variant="subtle"
                      mt={1}
                    >
                      {CONSULTATION_TYPES[selectedConsultation.tipo].label}
                    </Badge>
                  </Box>
                </Grid>

                <Divider />

                {/* Status e valor */}
                <Grid templateColumns="1fr 1fr" gap={4}>
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.500">
                      Status
                    </Text>
                    <Badge
                      colorScheme={CONSULTATION_STATUS[selectedConsultation.status].color}
                      variant="subtle"
                      mt={1}
                    >
                      {CONSULTATION_STATUS[selectedConsultation.status].label}
                    </Badge>
                  </Box>
                  

                </Grid>

                <Divider />

                {/* Prompt do Usuário */}
                <Box>
                  <Text fontSize="sm" fontWeight="medium" color="gray.500">
                    Prompt do Usuário
                  </Text>
                  <Text mt={1} whiteSpace="pre-wrap" color={selectedConsultation.prompt_usuario ? "inherit" : "gray.400"} fontStyle={selectedConsultation.prompt_usuario ? "normal" : "italic"}>
                    {selectedConsultation.prompt_usuario || "Nenhum prompt informado"}
                  </Text>
                </Box>

                {/* Resposta da IA */}
                <Box>
                  <Text fontSize="sm" fontWeight="medium" color="gray.500">
                    Resposta da IA
                  </Text>
                  <Box 
                    mt={1} 
                    p={3}
                    borderRadius="md"
                    bg={responseBgColor}
                    border="1px solid"
                    borderColor={responseBorderColor}
                    maxH="300px"
                    overflowY="auto"
                  >
                    {selectedConsultation.resposta_ia ? (
                      <div 
                        dangerouslySetInnerHTML={{ __html: selectedConsultation.resposta_ia }}
                        style={{ 
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          lineHeight: '1.5'
                        }}
                      />
                    ) : (
                      <Text color="gray.400" fontStyle="italic">
                        Nenhuma resposta gerada
                      </Text>
                    )}
                  </Box>
                </Box>

                {/* Prompt Utilizado */}
                <Box>
                  <Text fontSize="sm" fontWeight="medium" color="gray.500">
                    Prompt Utilizado
                  </Text>
                  <Text mt={1} color={(selectedConsultation.prompt_utilizado_nome || selectedConsultation.prompt_utilizado) ? "inherit" : "gray.400"} fontStyle={(selectedConsultation.prompt_utilizado_nome || selectedConsultation.prompt_utilizado) ? "normal" : "italic"}>
                    {selectedConsultation.prompt_utilizado_nome || selectedConsultation.prompt_utilizado || "Nenhum prompt utilizado"}
                  </Text>
                </Box>

                {/* Tempo de Resposta */}
                {selectedConsultation.tempo_resposta && (
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.500">
                      Tempo de Resposta
                    </Text>
                    <Text mt={1}>{selectedConsultation.tempo_resposta}ms</Text>
                  </Box>
                )}

                <Divider />

                {/* Duração */}
                {selectedConsultation.duracao && (
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.500">
                      Duração
                    </Text>
                    <Text mt={1}>{selectedConsultation.duracao} minutos</Text>
                  </Box>
                )}
              </VStack>
            )}
          </ModalBody>

          <ModalFooter>
            <Button colorScheme="medicare" onClick={onViewModalClose}>
              Fechar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default ConsultationsPage;